import { useState, useEffect, useRef } from "react";

interface UseAudioPreWarmReturn {
  isPreWarmed: boolean;
  isPermissionGranted: boolean;
}

export const useAudioPreWarm = (): UseAudioPreWarmReturn => {
  const [isPreWarmed, setIsPreWarmed] = useState(false);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const preWarmTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const preWarmAudioSystem = async () => {
      try {
        // Initialize audio context early to reduce first-time delay
        if (typeof window !== "undefined" && window.AudioContext) {
          const audioContext = new (window.AudioContext ||
            (window as any).webkitAudioContext)();

          // Create a short silent audio buffer to wake up the audio system
          const buffer = audioContext.createBuffer(1, 1, 22050);
          const source = audioContext.createBufferSource();
          source.buffer = buffer;
          source.connect(audioContext.destination);
          source.start();

          // Close the context after a short delay
          setTimeout(() => {
            audioContext.close();
          }, 100);
        }

        // Check microphone permission status without requesting access
        if (navigator.permissions && navigator.permissions.query) {
          const permission = await navigator.permissions.query({
            name: "microphone" as PermissionName,
          });

          setIsPermissionGranted(permission.state === "granted");

          if (permission.state === "granted") {
            // If already granted, pre-warm by briefly accessing the microphone
            preWarmTimeoutRef.current = setTimeout(async () => {
              try {
                const stream = await navigator.mediaDevices.getUserMedia({
                  audio: true,
                });
                // Quickly close the stream to just warm up the system
                stream.getTracks().forEach((track) => track.stop());
                setIsPreWarmed(true);
              } catch (error) {
                // Silently fail for pre-warming
                console.debug("Pre-warming failed:", error);
              }
            }, 500); // Delay to avoid blocking initial render
          }
        }
      } catch (error) {
        // Silently fail for pre-warming
        console.debug("Audio system pre-warming failed:", error);
      }
    };

    preWarmAudioSystem();

    // Cleanup
    return () => {
      if (preWarmTimeoutRef.current) {
        clearTimeout(preWarmTimeoutRef.current);
      }
    };
  }, []);

  return {
    isPreWarmed,
    isPermissionGranted,
  };
};
